/**
 * Why a dose was skipped — docs/33, B1.
 *
 * Skipping is the person's own answer, never a judgement, so the reason is
 * optional and comes from a short list of ordinary ones ("felt unwell",
 * "out of stock", "the doctor said", "other") with a few words of their own
 * if they want. It is kept in the dose's existing `note` column as
 * `code` or `code: words`, so no migration was needed and a note written by
 * a care-home nurse before this existed still reads as plain words.
 *
 * The code is rendered in each reader's language; the words are shown
 * exactly as typed.
 */
export const SKIP_REASONS = ['unwell', 'out_of_stock', 'doctor_said', 'other'] as const;
export type SkipReason = (typeof SKIP_REASONS)[number];

/** The longest a reason's own words may be (the note column takes 500). */
export const SKIP_WORDS_MAX = 200;

export interface SkipNote {
  reason: SkipReason | null;
  words: string | null;
}

/** `code`, `code: words`, `words`, or nothing — for the dose's `note`. */
export function formatSkipNote(reason: SkipReason | null | undefined, words?: string | null): string | null {
  const w = (words ?? '').trim().slice(0, SKIP_WORDS_MAX) || null;
  if (reason && w) return `${reason}: ${w}`;
  return reason ?? w;
}

/** The reverse. Anything that does not start with a known code is the person's own words. */
export function parseSkipNote(note: string | null | undefined): SkipNote {
  const n = (note ?? '').trim();
  if (!n) return { reason: null, words: null };
  for (const code of SKIP_REASONS) {
    if (n === code) return { reason: code, words: null };
    if (n.startsWith(`${code}: `)) return { reason: code, words: n.slice(code.length + 2).trim() || null };
  }
  return { reason: null, words: n };
}
